import { Pagination } from '$core/models/pagination';
import { ConfirmDeleteModalComponent } from '$core/modal/confirm-delete/confirm-delete.component';
import { User } from '$core/models/user';
import { LanguageService } from '$core/services/language.service';
import { SessionService } from '$core/services/session.service';
import { SwalService } from '$core/shell/swal/swal.service';
import { transition, trigger, useAnimation } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { fadeIn } from 'ng-animate';
import { BsModalService } from 'ngx-bootstrap/modal';
import { untilDestroyed } from '$core/operators/until-destroyed.operator';
import { filter, finalize, first, switchMap } from 'rxjs/operators';
import {
  BatchShort,
  DuplicateType,
  Selectable,
  BatchStatusEnum,
} from '../../models';
import { BatchesParseStorageService, BatchesService } from '../../services';
import { BehaviorSubject } from 'rxjs';
import { PaginationParams } from '$shared/components/paginator/paginator.component';
import { validateLocalStoragePerPage } from '$modules/personal/modules/batch/validateLocalStorageBatch';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'app-batch-list',
  templateUrl: './batch-list.component.html',
  styleUrls: ['./batch-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', useAnimation(fadeIn), {
        params: { timing: 0.2 },
      }),
    ]),
  ],
})
export class BatchListComponent implements OnInit, OnDestroy {
  public batches = new BehaviorSubject<Pagination<Selectable<BatchShort>>>(
    {} as Pagination<Selectable<BatchShort>>
  );
  public selected: BatchShort[] = [];
  public pageSizes: number[] = [5, 15, 25, 50];
  public queryParams: Params;
  public isLoading = false;
  public isLoadingFiles = false;

  public get user(): User {
    return this._sessionService.session;
  }

  constructor(
    private readonly _batchesService: BatchesService,
    private readonly _modalService: BsModalService,
    private readonly _translateService: TranslateService,
    private readonly _route: ActivatedRoute,
    private readonly _sessionService: SessionService,
    private readonly _router: Router,
    private readonly _swalService: SwalService,
    private readonly _toastr: ToastrService,
    private readonly _languageService: LanguageService,
    private readonly _batchStorageService: BatchesParseStorageService
  ) {}

  public ngOnDestroy(): void {}

  public ngOnInit(): void {
    this._batchStorageService.reset();
    if (localStorage.getItem('batch_items_per_page')) {
      this.queryParams = {
        ...this.queryParams,
        perPage: validateLocalStoragePerPage('batch_items_per_page'),
      };
    }
    this.listenToRouter();
  }

  // public download(): void {
  //   window.open(this.static.batch_mailing_template, '_blank');
  // }

  public create() {
    if (!this.user.hasLegalDataForBatches) {
      const swal = this._swalService.swal;
      swal.title = this._translateService.instant(
        'BATCH_MAILING.BATCHES_TAB.CREATION.TITLE'
      );
      swal.text = this._translateService.instant(
        'BATCH_MAILING.BATCHES_TAB.CREATION.TEXT'
      );
      swal.icon = 'error';
      swal.fire().then(() => {
        const url = this._languageService.translateRoute(
          '/personal/legal-addresses'
        );

        return this._router.navigateByUrl(url);
      });

      return;
    }

    return this._router.navigate(['create'], {
      relativeTo: this._route,
    });
  }

  public updateSelection(): void {
    if (this.batches && this.batches.value.data) {
      this.selected = this.batches.value.data.filter((el) => el.selected);
    }
  }

  public search(search: string | null) {
    return this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        perPage: this.queryParams.perPage,
        status: this.queryParams.status,
        search,
      },
    });
  }

  public remove(): void {
    const selected = this.batches.value.data
      .filter((el) => el.selected)
      .filter((el) => el.status !== BatchStatusEnum.IN_OPS).length;
    const inOpsSelected = this.batches.value.data
      .filter((el) => el.selected)
      .filter((el) => el.status === BatchStatusEnum.IN_OPS).length;

    if (!selected || !!inOpsSelected) {
      let text = this._translateService.instant(
        'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETING_PICK_ONE'
      );

      if (!!inOpsSelected) {
        text = this._translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETING_IN_OPS'
        );
      }

      this._toastr.warning(
        this._translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETING_NOT_EXIST'
        ),
        text
      );
      return;
    }

    const ids = this.batches.value.data
      .filter((el) => el.selected)
      .map((el) => el.id);
    this._modalService.show(ConfirmDeleteModalComponent, {
      ignoreBackdropClick: true,
      class: 'modal-dialog-centered',
      initialState: {
        title:
          this._translateService.instant(
            'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETE_BATCH'
          ) + '?',
        subTitle: this._translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.CONFIRM_DELETING'
        ),
        submitBtnClass: 'button--filled',
        cancelBtnClass: 'button--text',
      },
    });

    this._modalService.onHidden
      .pipe(
        first(),
        filter((res) => res === 'delete'),
        switchMap(() => this._batchesService.removeBatchList({ ids })),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.getItems();

        const swal = this._swalService.swal;
        swal.title = this._translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DELETED_BATCH'
        );
        swal.icon = 'success';
        return swal.fire();
      });
  }

  public duplicate(type: DuplicateType): void {
    const selected = this.batches.value.data.filter((el) => el.selected).length;
    if (!selected || selected > 1) {
      this._toastr.warning(
        this._translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.ACTIONS.DUPLICATE_NOT_EXIST'
        ),
        !selected
          ? this._translateService.instant(
              'BATCH_MAILING.BATCHES_TAB.ACTIONS.DUPLICATE_PICK_ONE'
            )
          : this._translateService.instant(
              'BATCH_MAILING.BATCHES_TAB.ACTIONS.DUPLICATE_ONLY_ONE'
            )
      );
      return;
    }
    const id = this.batches.value.data.find((el) => el.selected).id;
    this._batchesService.duplicate(id, type).subscribe((res) => {
      return this._router.navigate([res.id, 'view-batch'], {
        relativeTo: this._route,
      });
    });
  }

  public pageChanged(paginationEvent: PaginationParams) {
    return this._router.navigate([], {
      relativeTo: this._route,
      queryParams: { ...this.queryParams, ...paginationEvent },
    });
  }

  public async parse(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    if (target.files.length === 0) {
      return;
    }
    this.isLoadingFiles = true;
    const file = target.files[0];
    const formData = new FormData();
    formData.append('file', file);

    this._batchesService
      .parse(formData)
      .pipe(
        finalize(() => {
          target.value = null;
          this.isLoadingFiles = false;
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () =>
          this._router.navigate(['create'], {
            relativeTo: this._route,
          }),
        error: (error: HttpErrorResponse) => {
          this._toastr.error(error.error.message);
        },
      });
  }

  private listenToRouter(): void {
    this._route.queryParams.pipe(untilDestroyed(this)).subscribe((res) => {
      this.queryParams = {
        ...res,
        perPage: validateLocalStoragePerPage('batch_items_per_page'),
      };
      this.getItems();
    });
  }

  private getItems(): void {
    this.isLoading = true;
    this._batchesService
      .index(this.queryParams)
      .pipe(
        untilDestroyed(this),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe((res) => {
        this.batches.next({
          ...res,
          data: res.data.map((item) => ({ ...item, selected: false })),
        });
        this.updateSelection();
      });
  }

  public navigateToViewBatchPage(batch: BatchShort): void {
    let perPage = 15;

    if (localStorage.getItem('batch-departures-per-page')) {
      perPage = validateLocalStoragePerPage('batch-departures-per-page');
    }

    this._router.navigate([`/personal/batch/batches/${batch.id}/view-batch`], {
      queryParams: {
        perPage,
      },
    });
  }
}
